import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowDown, Zap, Droplets, Wheat, Utensils, Coffee, Beef, Fish,
  Stethoscope, GraduationCap, Home, Briefcase, Cpu, Factory, Mountain, Ship,
  Truck, Building2, Leaf, Globe2, Lightbulb, Check, Loader2, CheckCircle2, Sparkles,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const IMG = {
  hero: "https://images.unsplash.com/photo-1734865934450-719ef6f59a37?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  skyline: "https://images.unsplash.com/photo-1771350368994-9d87f0d8431f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  coffee: "https://images.unsplash.com/photo-1679917010073-ead631d41d91?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  land: "https://images.unsplash.com/photo-1573403092240-26095e118918?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  farm: "https://images.unsplash.com/photo-1572888195250-3037a59d3578?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  tech: "https://images.unsplash.com/photo-1655720357872-ce227e4164ba?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

const SUBNAV = [
  ["vision", "Vision"], ["needs", "Ethiopia's Needs"], ["solutions", "Solutions"],
  ["initiatives", "Initiatives"], ["diaspora", "Diaspora"], ["submit", "Submit an Idea"],
];

const CATEGORIES = [
  [Zap, "Energy", "Solar, battery storage, mini-grids and reliable electricity."],
  [Droplets, "Water & Sanitation", "Clean water, wells, filtration, storage, sanitation and irrigation."],
  [Wheat, "Agriculture", "Modern farming, irrigation, cold storage, processing and stronger farmer incomes."],
  [Utensils, "Food Security", "Local production, storage and distribution so families are fed year-round."],
  [Coffee, "Coffee", "Capture more value through processing, roasting, branding and international trade."],
  [Beef, "Livestock", "Healthier herds, better feed, veterinary care and market access."],
  [Fish, "Aquaculture", "Sustainable fish farming, aquaponics and domestic food production."],
  [Stethoscope, "Healthcare", "Clinics, access, equipment and trained health workers closer to home."],
  [GraduationCap, "Education", "Schools, vocational training, digital learning and skills for real jobs."],
  [Home, "Housing", "Affordable, durable homes and stronger local construction."],
  [Briefcase, "Jobs & Wages", "Industries, skills and businesses creating sustainable, better-paying work."],
  [Cpu, "Technology & Connectivity", "Internet access, devices and digital skills for communities."],
  [Factory, "Manufacturing", "Making more in Ethiopia — from raw material to finished product."],
  [Mountain, "Natural Resources", "Understanding resources and how they can create long-term value for Ethiopians."],
  [Ship, "Trade & Export", "Helping Ethiopian producers reach fair international markets."],
  [Truck, "Transportation", "Roads, logistics and cold-chain that connect farms to markets."],
  [Building2, "Infrastructure", "The foundations — power, water, roads — that everything else needs."],
  [Leaf, "Environment", "Reforestation, clean energy and sustainable land use."],
];

const PATHWAY = ["Problem", "Idea", "Research", "Feasibility", "Partners", "Funding", "Pilot", "Build", "Measure", "Scale"];

const RESOURCES = ["Coffee", "Agriculture", "Livestock", "Minerals", "Gold", "Gemstones",
  "Renewable Energy", "Water Resources", "Young People", "Entrepreneurs", "Culture", "Human Talent", "Global Diaspora"];

const INITIATIVES = [
  ["Affordable Solar Ethiopia", "Energy", "Researching"],
  ["Community Water Systems", "Water", "Idea"],
  ["Solar Irrigation", "Agriculture", "Idea"],
  ["Coffee Value Initiative", "Coffee", "Researching"],
  ["Aquaculture & Fish Farming", "Food", "Idea"],
  ["Affordable Housing", "Housing", "Feasibility"],
  ["Healthcare Access", "Healthcare", "Idea"],
  ["Vocational Education", "Education", "Seeking Partners"],
  ["Ethiopian Manufacturing", "Manufacturing", "Idea"],
  ["Natural Resource Value", "Resources", "Researching"],
  ["Trade & Export Development", "Trade", "Idea"],
  ["Agricultural Development", "Agriculture", "Idea"],
];

const DIASPORA_ROLES = ["Engineers", "Doctors", "Scientists", "Farmers", "Entrepreneurs", "Investors",
  "Educators", "Researchers", "Technologists", "Developers", "Builders", "Business Leaders"];

const CATEGORY_OPTIONS = ["Energy", "Water", "Agriculture", "Food", "Coffee", "Livestock", "Aquaculture",
  "Healthcare", "Education", "Housing", "Jobs", "Technology", "Manufacturing", "Natural Resources",
  "Trade", "Transportation", "Infrastructure", "Environment", "Other"];

const PARTICIPATION = ["I live in Ethiopia", "I am part of the Ethiopian diaspora", "I want to volunteer",
  "I can provide professional expertise", "I represent an organization", "I am interested in partnership",
  "I am interested in responsible investment opportunities"];

const BLANK_FORM = {
  category: "", region: "", city: "", nationwide: false, challenge: "", idea: "", benefit: "",
  name: "", email: "", phone: "", organization: "", profession: "", expertise: "", participation: [],
};

const inputCls = "w-full border border-white/20 rounded-lg px-4 py-3 text-[15px] bg-white/5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#E0A526]/60 focus:border-[#E0A526]";

const badgeColor = (s) => ({
  Idea: "bg-white/10 text-white/70", Researching: "bg-[#E0A526]/25 text-[#E0A526]",
  Feasibility: "bg-blue-400/20 text-blue-200", "Seeking Partners": "bg-purple-400/20 text-purple-200",
}[s] || "bg-white/10 text-white/70");

export default function ABEthiopiaPage() {
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (p) => setForm((f) => ({ ...f, participation: f.participation.includes(p) ? f.participation.filter((x) => x !== p) : [...f.participation, p] }));
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/abe-ideas", form);
      setForm(BLANK_FORM);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="bg-[#0B1F14] min-h-[80vh] flex items-center justify-center px-6 py-24" data-testid="abe-thankyou">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-xl text-center text-white">
          <span className="w-20 h-20 rounded-full bg-[#E0A526]/20 text-[#E0A526] flex items-center justify-center mx-auto mb-6"><CheckCircle2 size={40} /></span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold mb-4">Thank you for sharing your idea.</h1>
          <p className="text-lg text-white/80 leading-relaxed">Every solution begins somewhere. Your submission has been received and will be reviewed as part of the A Better Ethiopia initiative.</p>
          <Link to="/a-better-ethiopia" onClick={() => setDone(false)} className="mt-8 inline-flex items-center gap-2 bg-[#E0A526] text-[#0B1F14] rounded-full px-8 py-4 font-semibold hover:bg-white transition-colors" data-testid="abe-back-btn">Back to A Better Ethiopia <ArrowRight size={18} /></Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-[#0B1F14] text-white overflow-x-hidden" data-testid="abe-page">
      {/* HERO */}
      <section className="relative min-h-[86vh] flex items-center overflow-hidden">
        <img src={IMG.hero} alt="Modern Addis Ababa, Ethiopia" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1F14] via-[#0B1F14]/85 to-[#0B1F14]/30" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-28">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-3xl">
            <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#E0A526] mb-5">A RefugeM Initiative</p>
            <h1 className="font-display text-6xl sm:text-7xl font-bold leading-[0.92] tracking-tight">A Better Ethiopia</h1>
            <p className="font-display text-2xl sm:text-3xl text-white/90 mt-6 leading-tight">What if we stopped asking what's wrong — and started asking what's possible?</p>
            <p className="text-lg text-white/70 mt-6 leading-relaxed max-w-2xl">RefugeM's work begins with vulnerable children. But our vision doesn't end there. We believe helping the next generation also means strengthening the families, communities, opportunities and systems surrounding them.</p>
            <div className="flex flex-wrap gap-4 mt-9">
              <button onClick={() => go("submit")} data-testid="abe-hero-submit" className="inline-flex items-center gap-2 bg-[#E0A526] text-[#0B1F14] rounded-full px-8 py-4 font-semibold tracking-wide hover:bg-white transition-colors">Submit your idea <ArrowRight size={18} /></button>
              <button onClick={() => go("vision")} data-testid="abe-hero-explore" className="inline-flex items-center gap-2 border border-white/40 rounded-full px-8 py-4 font-semibold hover:bg-white/10 transition-colors">Explore the vision <ArrowDown size={18} /></button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SUBNAV */}
      <nav className="sticky top-24 z-30 bg-[#0B1F14]/90 backdrop-blur border-y border-white/10" data-testid="abe-subnav">
        <div className="max-w-7xl mx-auto px-4 lg:px-10 flex gap-1 overflow-x-auto hide-scrollbar">
          {SUBNAV.map(([id, label]) => (
            <button key={id} onClick={() => go(id)} data-testid={`abe-nav-${id}`} className="whitespace-nowrap px-4 py-3.5 text-sm font-medium text-white/70 hover:text-[#E0A526] transition-colors">{label}</button>
          ))}
        </div>
      </nav>

      {/* VISION / BIGGER QUESTION */}
      <section id="vision" className="scroll-mt-40 max-w-5xl mx-auto px-6 lg:px-10 py-28 text-center">
        <motion.h2 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="font-display text-3xl sm:text-4xl font-semibold text-white/60">We can help a child today.</motion.h2>
        <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }} className="font-display text-4xl sm:text-6xl font-bold mt-6 leading-[1.05]">But what can we build today so that child needs <span className="text-[#E0A526]">less help tomorrow?</span></motion.p>
        <div className="flex flex-wrap justify-center gap-2.5 mt-12">
          {["Clean Water", "Reliable Electricity", "Food Security", "Healthcare", "Education", "Housing", "Jobs", "Technology", "Strong Businesses", "Infrastructure", "Economic Opportunity"].map((t) => (
            <span key={t} className="text-sm font-medium bg-white/8 border border-white/15 rounded-full px-4 py-1.5">{t}</span>
          ))}
        </div>
        <p className="text-white/70 mt-10 max-w-2xl mx-auto leading-relaxed">These aren't separate from helping children. They are part of creating an environment where children and families can thrive.</p>
        <p className="mt-8 font-display text-lg tracking-wide text-[#E0A526]">Children → Families → Communities → Ethiopia</p>
      </section>

      {/* NEEDS / CATEGORIES */}
      <section id="needs" className="scroll-mt-40 bg-[#0F2A1B] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="max-w-2xl mb-14">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#E0A526] mb-3">Ethiopia's needs</p>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-tight">Let's start with the challenges.</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CATEGORIES.map(([Icon, name, desc], i) => (
              <motion.div key={name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
                onClick={() => go("submit")} data-testid={`abe-category-${name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                className="group bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 rounded-xl p-6 cursor-pointer transition-colors">
                <span className="w-12 h-12 rounded-full bg-[#E0A526]/15 text-[#E0A526] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"><Icon size={22} /></span>
                <h3 className="font-display text-xl font-semibold mb-1">{name}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{desc}</p>
                <span className="text-xs text-[#E0A526] mt-3 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">Have an idea? <ArrowRight size={13} /></span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTIONS / PATHWAY */}
      <section id="solutions" className="scroll-mt-40 max-w-6xl mx-auto px-6 lg:px-10 py-28 text-center">
        <h2 className="font-display text-4xl sm:text-5xl font-semibold">Don't just tell us the problem.</h2>
        <p className="font-display text-4xl sm:text-5xl font-bold text-[#E0A526] mt-2">Help us build the solution.</p>
        <div className="flex flex-col md:flex-row md:flex-wrap items-stretch md:items-center justify-center gap-2 mt-14">
          {PATHWAY.map((step, i) => (
            <React.Fragment key={step}>
              <div className="bg-white/[0.05] border border-white/10 rounded-lg px-4 py-3 text-sm font-semibold min-w-[120px]">{step}</div>
              {i < PATHWAY.length - 1 && <ArrowRight size={16} className="text-[#E0A526] mx-auto rotate-90 md:rotate-0" />}
            </React.Fragment>
          ))}
        </div>
        <p className="text-white/70 mt-12 max-w-2xl mx-auto leading-relaxed">Every major project starts when someone recognizes that something can be better.</p>
        <button onClick={() => go("submit")} data-testid="abe-solutions-cta" className="mt-8 inline-flex items-center gap-2 bg-[#E0A526] text-[#0B1F14] rounded-full px-8 py-4 font-semibold hover:bg-white transition-colors">Submit an idea <ArrowRight size={18} /></button>
      </section>

      {/* RESOURCES */}
      <section className="relative overflow-hidden border-y border-white/10">
        <img src={IMG.coffee} alt="Ethiopian coffee" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-[#0B1F14]/80" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-28">
          <h2 className="font-display text-4xl sm:text-5xl font-semibold">Ethiopia has resources.</h2>
          <div className="flex flex-wrap gap-2.5 mt-8 max-w-4xl">
            {RESOURCES.map((r) => <span key={r} className="text-sm font-medium bg-[#E0A526]/15 text-[#E0A526] border border-[#E0A526]/25 rounded-full px-4 py-1.5">{r}</span>)}
          </div>
          <p className="text-white/60 mt-12 text-lg">The question isn't only: <span className="text-white">"What does Ethiopia have?"</span></p>
          <p className="font-display text-3xl sm:text-4xl font-bold mt-3">The bigger question is: <span className="text-[#E0A526]">"What can Ethiopia build with it?"</span></p>
        </div>
      </section>

      {/* IMAGINE WHAT'S POSSIBLE */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-28">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#E0A526] mb-3">Imagine what's possible</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
          <div className="grid grid-cols-2 gap-4">
            {[IMG.farm, IMG.tech, IMG.land, IMG.skyline].map((src, i) => (
              <div key={i} className={`overflow-hidden rounded-xl ${i % 2 ? "mt-8" : ""}`}>
                <img src={src} alt="A better Ethiopia" className="w-full h-56 object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
            ))}
          </div>
          <div className="space-y-3">
            {["Grown in Ethiopia.", "Processed in Ethiopia.", "Made in Ethiopia.", "Built by Ethiopians.", "Shared with the world."].map((t, i) => (
              <motion.p key={t} initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className={`font-display text-3xl sm:text-4xl font-bold ${i === 4 ? "text-[#E0A526]" : ""}`}>{t}</motion.p>
            ))}
            <p className="text-white/70 pt-4 leading-relaxed">Ethiopia should not only export resources. Where practical, Ethiopia should increasingly create value from those resources through Ethiopian farming, processing, manufacturing, businesses, jobs and brands.</p>
          </div>
        </div>
      </section>

      {/* INITIATIVES */}
      <section id="initiatives" className="scroll-mt-40 bg-[#0F2A1B] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="max-w-2xl mb-4">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#E0A526] mb-3">From ideas to initiatives</p>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-tight">Early research concepts.</h2>
          </div>
          <p className="text-white/60 mb-12 max-w-2xl text-sm">These are research concepts — not approved, funded, government-backed or active projects. Each will show an honest status as it develops.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {INITIATIVES.map(([name, cat, status]) => (
              <div key={name} className="bg-white/[0.04] border border-white/10 rounded-xl p-6" data-testid={`abe-initiative-${name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[11px] tracking-[0.12em] uppercase font-bold text-white/50">{cat}</span>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full uppercase tracking-wider font-bold ${badgeColor(status)}`}>{status}</span>
                </div>
                <h3 className="font-display text-lg font-semibold">{name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DIASPORA */}
      <section id="diaspora" className="scroll-mt-40 max-w-7xl mx-auto px-6 lg:px-10 py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <Globe2 size={40} className="text-[#E0A526] mb-6" />
            <h2 className="font-display text-4xl sm:text-5xl font-bold leading-tight">One country. A global network.</h2>
            <p className="font-display text-xl text-[#E0A526] mt-4">Ethiopia's diaspora can help build what comes next.</p>
            <p className="text-white/70 mt-6 leading-relaxed">Wherever you live, your knowledge, experience, relationships, resources and ideas may help build something meaningful in Ethiopia.</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <button onClick={() => go("submit")} data-testid="abe-diaspora-expertise" className="inline-flex items-center gap-2 bg-[#E0A526] text-[#0B1F14] rounded-full px-7 py-3.5 font-semibold hover:bg-white transition-colors">Offer your expertise <ArrowRight size={17} /></button>
              <Link to="/join" data-testid="abe-diaspora-partner" className="inline-flex items-center gap-2 border border-white/40 rounded-full px-7 py-3.5 font-semibold hover:bg-white/10 transition-colors">Become a partner <ArrowRight size={17} /></Link>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {DIASPORA_ROLES.map((r) => (
              <div key={r} className="bg-white/[0.05] border border-white/10 rounded-lg px-3 py-4 text-center text-sm font-medium">{r}</div>
            ))}
          </div>
        </div>
      </section>

      {/* GUIDING PRINCIPLE */}
      <section className="bg-[#E0A526] text-[#0B1F14]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24 text-center">
          <h2 className="font-display text-4xl sm:text-5xl font-bold leading-tight">Nothing about Ethiopia without Ethiopia.</h2>
          <p className="mt-6 leading-relaxed text-[#0B1F14]/80 max-w-2xl mx-auto">A Better Ethiopia isn't about outsiders arriving with all the answers. We begin by listening. Local communities, farmers, workers, young people, professionals, businesses, the diaspora and future generations all matter. Solutions for Ethiopia should place Ethiopians at the center.</p>
        </div>
      </section>

      {/* SUBMIT FORM */}
      <section id="submit" className="scroll-mt-40 max-w-3xl mx-auto px-6 lg:px-10 py-28">
        <div className="text-center mb-4">
          <Lightbulb size={40} className="text-[#E0A526] mx-auto mb-4" />
          <h2 className="font-display text-4xl sm:text-5xl font-bold">What's your idea for a better Ethiopia?</h2>
        </div>
        <p className="text-white/70 text-center max-w-2xl mx-auto mb-10 leading-relaxed">Maybe you see a problem. Maybe you have a solution, expertise, a technology, or someone we should speak with. Or maybe you simply have an idea nobody has considered yet. We want to hear it.</p>

        <form onSubmit={submit} className="bg-white/[0.04] border border-white/10 rounded-2xl p-7 sm:p-10 space-y-6" data-testid="abe-form">
          <label className="block"><span className="text-sm font-semibold">What does your idea relate to?</span>
            <select required className={`${inputCls} mt-1.5`} value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="abe-category">
              <option value="" className="text-black">Choose a category…</option>
              {CATEGORY_OPTIONS.map((c) => <option key={c} className="text-black">{c}</option>)}
            </select></label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <label className="block"><span className="text-sm font-semibold">Region</span>
              <input className={`${inputCls} mt-1.5`} value={form.region} onChange={(e) => set("region", e.target.value)} data-testid="abe-region" /></label>
            <label className="block"><span className="text-sm font-semibold">City / Town / Village</span>
              <input className={`${inputCls} mt-1.5`} value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="abe-city" /></label>
          </div>
          <label className="flex items-center gap-2.5 text-sm cursor-pointer">
            <input type="checkbox" checked={form.nationwide} onChange={(e) => set("nationwide", e.target.checked)} data-testid="abe-nationwide" className="w-4 h-4 accent-[#E0A526]" /> This applies nationwide
          </label>

          <label className="block"><span className="text-sm font-semibold">What's the challenge?</span>
            <textarea rows={2} className={`${inputCls} mt-1.5`} value={form.challenge} onChange={(e) => set("challenge", e.target.value)} data-testid="abe-challenge" /></label>
          <label className="block"><span className="text-sm font-semibold">What's your idea?</span>
            <textarea rows={3} className={`${inputCls} mt-1.5`} value={form.idea} onChange={(e) => set("idea", e.target.value)} data-testid="abe-idea" /></label>
          <label className="block"><span className="text-sm font-semibold">How could this help?</span>
            <textarea rows={2} className={`${inputCls} mt-1.5`} value={form.benefit} onChange={(e) => set("benefit", e.target.value)} data-testid="abe-benefit" /></label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <label className="block"><span className="text-sm font-semibold">Name *</span>
              <input required className={`${inputCls} mt-1.5`} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="abe-name" /></label>
            <label className="block"><span className="text-sm font-semibold">Email *</span>
              <input required type="email" className={`${inputCls} mt-1.5`} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="abe-email" /></label>
            <label className="block"><span className="text-sm font-semibold">Phone / WhatsApp</span>
              <input type="tel" className={`${inputCls} mt-1.5`} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="abe-phone" /></label>
            <label className="block"><span className="text-sm font-semibold">Organization <span className="text-white/40 font-normal">(optional)</span></span>
              <input className={`${inputCls} mt-1.5`} value={form.organization} onChange={(e) => set("organization", e.target.value)} data-testid="abe-org" /></label>
            <label className="block"><span className="text-sm font-semibold">Profession <span className="text-white/40 font-normal">(optional)</span></span>
              <input className={`${inputCls} mt-1.5`} value={form.profession} onChange={(e) => set("profession", e.target.value)} data-testid="abe-profession" /></label>
            <label className="block"><span className="text-sm font-semibold">Expertise <span className="text-white/40 font-normal">(optional)</span></span>
              <input className={`${inputCls} mt-1.5`} value={form.expertise} onChange={(e) => set("expertise", e.target.value)} data-testid="abe-expertise" /></label>
          </div>

          <div>
            <span className="text-sm font-semibold">How would you like to participate?</span>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
              {PARTICIPATION.map((p) => (
                <button type="button" key={p} onClick={() => toggle(p)} data-testid={`abe-part-${p.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                  className={`flex items-center gap-2.5 text-left rounded-lg border px-3.5 py-2.5 text-[13px] transition-colors ${form.participation.includes(p) ? "bg-[#E0A526] text-[#0B1F14] border-[#E0A526]" : "border-white/20 hover:border-white/50"}`}>
                  <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${form.participation.includes(p) ? "bg-[#0B1F14] border-[#0B1F14]" : "border-white/40"}`}>{form.participation.includes(p) && <Check size={11} className="text-[#E0A526]" />}</span>
                  {p}
                </button>
              ))}
            </div>
          </div>

          <button type="submit" disabled={busy} data-testid="abe-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#E0A526] text-[#0B1F14] rounded-full py-4 font-semibold tracking-wide hover:bg-white transition-colors disabled:opacity-60">
            {busy ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : <>Start the conversation <ArrowRight size={18} /></>}
          </button>
        </form>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden border-t border-white/10">
        <img src={IMG.skyline} alt="A better Ethiopia" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-[#0B1F14]/85" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 py-28 text-center">
          <Sparkles size={36} className="text-[#E0A526] mx-auto mb-6" />
          <h2 className="font-display text-4xl sm:text-5xl font-bold leading-tight">Have an idea? See a problem? Know a solution?</h2>
          <p className="font-display text-3xl text-[#E0A526] mt-3">Let's talk.</p>
          <p className="text-white/70 mt-6">A Better Ethiopia begins with all of us.</p>
          <button onClick={() => go("submit")} data-testid="abe-final-cta" className="mt-8 inline-flex items-center gap-2 bg-[#E0A526] text-[#0B1F14] rounded-full px-9 py-4 font-semibold hover:bg-white transition-colors">Start the conversation <ArrowRight size={18} /></button>
          <p className="font-display text-2xl sm:text-3xl font-bold mt-14 leading-tight">Don't just imagine a better Ethiopia.<br /><span className="text-[#E0A526]">Help build it.</span></p>
        </div>
      </section>
    </div>
  );
}
